/**
 * Календарь доступности для формы брони (manager v3 §4.1, правки Артёма).
 *
 * Зелёный = ночь свободна для ВЫБРАННОГО номера (нет occupancy, покрывающего
 * этот день), её можно выбрать. Серые/перечёркнутые = заняты или в прошлом —
 * выбрать нельзя. Менеджер ставит ДВЕ даты: заезд → выезд. Правила:
 *
 *   • первую дату (заезд) можно ставить только на зелёную ночь;
 *   • вторую дату (выезд) можно ставить только так, чтобы ВЕСЬ диапазон
 *     ночей [заезд, выезд) был свободен (ни одной занятой ночи внутри);
 *   • выезд в день начала следующей брони допустим (интервал полуоткрытый).
 *
 * Занятость берётся из occupancies выбранного номера (CalendarRoom). Даты —
 * по локальному календарю (isoDate), консистентно с шахматкой.
 *
 * i18n: всё русское. Мобайл 375: сетка 7 колонок ужимается, кнопки ≥ 40px.
 */
import { useMemo, useState } from 'react';
import type { CalendarOccupancy } from '@hsc/types';
import { isoDate } from '../../lib/fleet-format.js';

const WEEKDAY_HEAD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

/** Дата (полночь, локально) → YYYY-MM-DD. */
function dayIso(year: number, month: number, day: number): string {
  return isoDate(new Date(year, month, day));
}

/** День покрыт occupancy (ночь занята): from <= day < to (полуоткрытый). */
function dayBusy(iso: string, occupancies: CalendarOccupancy[]): boolean {
  for (const occ of occupancies) {
    const from = occ.from.slice(0, 10);
    const to = occ.to.slice(0, 10);
    if (iso >= from && iso < to) return true;
  }
  return false;
}

/** Сколько ячеек-заполнителей слева до первого дня месяца (Пн=0…Вс=6). */
function leadingBlanks(year: number, month: number): number {
  const dow = new Date(year, month, 1).getDay(); // 0=Вс…6=Сб
  return (dow + 6) % 7; // → Пн=0
}

/** Все занятые ночи строго между [from, to) (исключая саму to). */
function rangeHasBusyNight(
  fromIso: string,
  toIso: string,
  occupancies: CalendarOccupancy[],
): boolean {
  const cur = new Date(`${fromIso}T00:00:00`);
  const end = new Date(`${toIso}T00:00:00`);
  let guard = 0;
  while (cur < end && guard < 400) {
    if (dayBusy(isoDate(cur), occupancies)) return true;
    cur.setDate(cur.getDate() + 1);
    guard += 1;
  }
  return false;
}

export interface AvailabilityCalendarProps {
  /** Занятость выбранного номера. */
  occupancies: CalendarOccupancy[];
  /** Текущий выбранный заезд (YYYY-MM-DD) или ''. */
  from: string;
  /** Текущий выбранный выезд (YYYY-MM-DD) или ''. */
  to: string;
  /** Менеджер выбрал дату — компонент сам решает, заезд это или выезд. */
  onPick: (next: { from: string; to: string }) => void;
}

/**
 * Зелёный календарь доступности. Логика выбора:
 *  - нет заезда ИЛИ есть и заезд и выезд → клик задаёт новый заезд (сброс выезда);
 *  - есть заезд, нет выезда:
 *      • клик ≤ заезд → переставить заезд;
 *      • клик > заезд и диапазон [заезд, клик) без занятых ночей → задать выезд;
 *      • иначе (между заняты) → начать заново с этой даты как заезд (если свободна).
 */
export function AvailabilityCalendar({
  occupancies,
  from,
  to,
  onPick,
}: AvailabilityCalendarProps): JSX.Element {
  const today = useMemo(() => isoDate(new Date()), []);
  // Месяц навигации: стартуем с месяца заезда (или текущего).
  const initial = from !== '' ? new Date(`${from}T00:00:00`) : new Date();
  const [cursor, setCursor] = useState({ y: initial.getFullYear(), m: initial.getMonth() });

  const blanks = leadingBlanks(cursor.y, cursor.m);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();

  // Фаза «выбираем выезд»: заезд задан, выезд ещё нет.
  const pickingCheckout = from !== '' && to === '';

  const handleClick = (iso: string): void => {
    // Старт нового выбора: нет заезда ИЛИ диапазон уже полон.
    if (from === '' || (from !== '' && to !== '')) {
      onPick({ from: iso, to: '' });
      return;
    }
    // Есть заезд, выбираем выезд.
    if (iso <= from) {
      // Клик на сам заезд или раньше — переставляем заезд.
      onPick({ from: iso, to: '' });
      return;
    }
    // iso > from: годен как выезд, только если ночи [from, iso) все свободны
    // (сам день выезда не входит — полуоткрытый интервал, поэтому выезд в
    // день начала следующей брони допустим).
    if (!rangeHasBusyNight(from, iso, occupancies)) {
      onPick({ from, to: iso });
    }
    // Иначе клик игнорируем (между лежит занятая ночь) — кнопка и так disabled.
  };

  /** Можно ли кликнуть на этот день при текущем состоянии выбора. */
  const isSelectable = (iso: string, busy: boolean, past: boolean): boolean => {
    if (past) return false;
    // Фаза выбора выезда: день — это дата выезда, его ночь НЕ входит в
    // проживание. Поэтому допускаем даже занятый день (старт след. брони),
    // если все ночи [заезд, выезд) свободны.
    if (pickingCheckout && iso > from) {
      return !rangeHasBusyNight(from, iso, occupancies);
    }
    // Фаза выбора заезда (или клик ≤ заезд для перестановки): заезд = ночь,
    // занятую ночь брать нельзя.
    if (busy) return false;
    return true;
  };

  const cells: Array<{ iso: string; day: number } | null> = [];
  for (let i = 0; i < blanks; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ iso: dayIso(cursor.y, cursor.m, d), day: d });
  }

  const prevMonth = (): void =>
    setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const nextMonth = (): void =>
    setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));

  return (
    <div className="avc" data-testid="availability-calendar">
      <div className="avc__head">
        <button
          type="button"
          className="avc__nav"
          onClick={prevMonth}
          aria-label="Предыдущий месяц"
          data-testid="avc-prev"
        >
          ←
        </button>
        <span className="avc__title" data-testid="avc-month">
          {MONTHS[cursor.m]} {cursor.y}
        </span>
        <button
          type="button"
          className="avc__nav"
          onClick={nextMonth}
          aria-label="Следующий месяц"
          data-testid="avc-next"
        >
          →
        </button>
      </div>

      <div className="avc__weekdays" aria-hidden="true">
        {WEEKDAY_HEAD.map((w) => (
          <span key={w} className="avc__wd">
            {w}
          </span>
        ))}
      </div>

      <div className="avc__grid" role="grid" aria-label="Календарь доступности">
        {cells.map((cell, idx) => {
          if (cell === null) return <span key={`b${idx}`} className="avc__blank" />;
          const busy = dayBusy(cell.iso, occupancies);
          const past = cell.iso < today;
          const selectable = isSelectable(cell.iso, busy, past);
          const isFrom = cell.iso === from;
          const isTo = cell.iso === to;
          const inRange = from !== '' && to !== '' && cell.iso > from && cell.iso < to;
          // Занятый день, доступный как ВЫЕЗД (старт след. брони, ночь не
          // входит) — помечаем is-busy, но кликабельным (is-checkout-ok).
          const checkoutOnBusy = busy && selectable;
          const cls = [
            'avc__day',
            busy ? 'is-busy' : selectable ? 'is-free' : 'is-blocked',
            checkoutOnBusy ? 'is-checkout-ok' : '',
            isFrom ? 'is-from' : '',
            isTo ? 'is-to' : '',
            inRange ? 'is-in-range' : '',
          ]
            .filter(Boolean)
            .join(' ');
          const title = checkoutOnBusy
            ? 'Доступно как дата выезда (следующая бронь начинается в этот день)'
            : busy
              ? 'Занято'
              : past
                ? 'Прошедшая дата'
                : selectable
                  ? 'Свободно'
                  : 'Недоступно (внутри диапазона есть занятая ночь)';
          return (
            <button
              key={cell.iso}
              type="button"
              className={cls}
              role="gridcell"
              disabled={!selectable}
              aria-pressed={isFrom || isTo}
              title={title}
              data-testid={`avc-day-${cell.iso}`}
              onClick={() => handleClick(cell.iso)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      <div className="avc__legend" aria-hidden="true">
        <span className="avc__lg">
          <span className="avc__sw avc__sw--free" /> Свободно
        </span>
        <span className="avc__lg">
          <span className="avc__sw avc__sw--busy" /> Занято
        </span>
        <span className="avc__lg">
          <span className="avc__sw avc__sw--sel" /> Выбрано
        </span>
      </div>
    </div>
  );
}
